import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Modal, Icon, Alert } from "@ann/web-components";
import {
  HeaderPhoneModalContainer,
  PhoneItemsWrapper,
  AddPhoneContainer,
  PhoneInputContainer,
  PhoneItemContainer,
  SideIconWrapper,
  RadioButton,
  ValidationMessage,
  FloatingAlertContainer,
} from "./HeaderPhoneModal.styled";
import InputText from "../../../../../elements/v2/TextInput/TextInput.jsx";
import { useContacts } from "../../context/ContactsContext";

const HeaderPhoneModal = ({ isOpen, onClose, onRequestRemove }) => {
  const {
    phones: contextPhones,
    createPhone,
    updatePhone,
    deletePhone,
    selectedContact,
    fetchContactPhones,
    applyPrimaryPhoneLocally,
  } = useContacts();

  const contactId = selectedContact?.partyUuid;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const [selectedPhoneId, setSelectedPhoneId] = useState(null);
  const [phones, setPhones] = useState([]);
  const [phoneErrors, setPhoneErrors] = useState({});
  const [validPhones, setValidPhones] = useState({});
  const [statusMessage, setStatusMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSyncedOnOpen, setHasSyncedOnOpen] = useState(false);

  const phoneRegex = useMemo(() => /^1?\d{10}$/, []);
  const e164Regex = useMemo(() => /^\+[1-9]\d{7,14}$/, []);

  const successTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    };
  }, []);

  // Sync al abrir/cerrar
  useEffect(() => {
    if (isOpen && contactId && !hasSyncedOnOpen) {
      fetchContactPhones(contactId);
      setHasSyncedOnOpen(true);
    }
    if (!isOpen) {
      setHasSyncedOnOpen(false);
      setShowSuccessAlert(false);
      setSuccessMessage("");
      setStatusMessage("");
      setPhones([]);
      setSelectedPhoneId(null);
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    }
  }, [isOpen, contactId, hasSyncedOnOpen, fetchContactPhones]);

  useEffect(() => {
    if (
      isOpen &&
      contextPhones &&
      contextPhones.length > 0 &&
      phones.length === 0
    ) {
      const mapped = contextPhones.map((p) => ({
        id: p.partyPhoneUuid,
        phone: p.phoneNumber,
        isEditing: false,
        isPrimary: p.isPrimary,
        isNew: false,
      }));
      setPhones(mapped);
      const primary = mapped.find((p) => p.isPrimary);
      if (primary) setSelectedPhoneId(primary.id);
    }
  }, [isOpen, contextPhones, phones.length]);

  useEffect(() => {
    const existingStyle = document.getElementById("modal-custom-width");
    if (isOpen && !existingStyle) {
      const style = document.createElement("style");
      style.id = "modal-custom-width";
      style.textContent = `
        [role="dialog"] {
          width: 500px !important;
          max-width: 500px !important;
          min-width: 500px !important;
        }
      `;
      document.head.appendChild(style);
    } else if (!isOpen && existingStyle) {
      existingStyle.remove();
    }
    return () => {
      const cleanupStyle = document.getElementById("modal-custom-width");
      if (cleanupStyle) cleanupStyle.remove();
    };
  }, [isOpen]);

  const handlePhoneSelection = useCallback((value) => {
    setSelectedPhoneId(value);
  }, []);

  const handleDeletePhone = useCallback(
    (phoneId) => {
      if (!contactId) return;

      const phoneToDelete = phones.find((p) => p.id === phoneId);
      if (onRequestRemove) {
        onRequestRemove(phoneId, phoneToDelete?.phone ?? "");
        return;
      }

      setIsDeleting(true);
      (async () => {
        try {
          await deletePhone(contactId, phoneId);
          setPhones((prev) => {
            const updated = prev.filter((p) => p.id !== phoneId);
            if (selectedPhoneId === phoneId) {
              if (updated.length > 0) {
                setSelectedPhoneId(updated[0].id);
              } else {
                setSelectedPhoneId(null);
              }
            }
            return updated;
          });
          setStatusMessage("");
          setSuccessMessage("");
          setShowSuccessAlert(false);
        } catch (err) {
          setShowErrorAlert(true);
          setStatusMessage("Error deleting phone. Please try again.");
          console.error("Error deleting phone:", err);
        } finally {
          setIsDeleting(false);
        }
      })();
    },
    [contactId, deletePhone, selectedPhoneId, phones, onRequestRemove]
  );

  const handlePhoneChange = useCallback(
    (phoneId, newValue) => {
      const trimmed = newValue.trim();
      const cleanPhone = trimmed.replace(/\D/g, "");
      const isValid = cleanPhone && phoneRegex.test(cleanPhone);

      setPhones((prev) =>
        prev.map((phone) =>
          phone.id === phoneId ? { ...phone, phone: newValue } : phone
        )
      );
      setValidPhones((prev) => ({ ...prev, [phoneId]: isValid }));

      if (isValid) {
        setPhoneErrors((prev) => {
          const updated = { ...prev };
          delete updated[phoneId];
          return updated;
        });
      }
    },
    [phoneRegex]
  );

  const handleConfirmPhone = useCallback((phoneId) => {
    setPhones((prev) =>
      prev.map((p) => (p.id === phoneId ? { ...p, isEditing: false } : p))
    );
    setPhoneErrors((prev) => {
      const updated = { ...prev };
      delete updated[phoneId];
      return updated;
    });
  }, []);

  const handleKeyDown = useCallback(
    (e, phoneId) => {
      if (e.key !== "Enter") return;
      e.preventDefault();

      const phone = phones.find((p) => p.id === phoneId)?.phone.trim();
      if (!phone) {
        setPhoneErrors((prev) => ({
          ...prev,
          [phoneId]: "Phone number is required",
        }));
        return;
      }

      const clean = phone.replace(/\D/g, "");
      if (!phoneRegex.test(clean)) {
        setPhoneErrors((prev) => ({
          ...prev,
          [phoneId]: "The number is incorrect, please try with other",
        }));
        return;
      }

      setValidPhones((prev) => ({ ...prev, [phoneId]: true }));
      handleConfirmPhone(phoneId);
    },
    [phones, phoneRegex, handleConfirmPhone]
  );

  const canAdd = useMemo(
    () => !phones.some((p) => p.isEditing || phoneErrors[p.id]),
    [phones, phoneErrors]
  );

  const canSave = useMemo(() => {
    const hasEditing = phones.some((p) => p.isEditing);
    const hasErrors = Object.values(phoneErrors).some(Boolean);
    return !hasEditing && !hasErrors;
  }, [phones, phoneErrors]);

  const handleAddPhone = useCallback(() => {
    if (!canAdd) {
      setShowErrorAlert(true);
      return;
    }
    const newId = `phone${Date.now()}`;
    setPhones((prev) => [
      ...prev,
      { id: newId, phone: "", isEditing: true, isNew: true },
    ]);
  }, [canAdd]);

  const toE164 = (raw) => {
    let cleaned = (raw || "").trim().replace(/\D/g, "");
    if ((raw || "").trim().startsWith("+")) return (raw || "").trim();
    if (cleaned.length === 10) return `+1${cleaned}`;
    if (cleaned.length === 11 && cleaned.startsWith("1")) return `+${cleaned}`;
    return `+${cleaned}`;
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (!contactId) return;

    setIsSaving(true);
    try {
      const phonesToCreate = phones.filter(
        (p) =>
          p.isNew && (validPhones[p.id] || (p.phone && p.phone.trim() !== ""))
      );

      const originalPrimaryOutside = contextPhones.find(
        (cp) => cp.isPrimary && cp.partyPhoneUuid !== selectedPhoneId
      );

      const phonesToUpdate = phones.filter((p) => {
        if (p.isNew) return false;

        if (
          originalPrimaryOutside &&
          p.id === originalPrimaryOutside.partyPhoneUuid
        ) {
          return false;
        }

        if (p.isEditing && validPhones[p.id]) return true;

        const original = contextPhones.find((cp) => cp.partyPhoneUuid === p.id);
        if (original) {
          const newIsPrimary = selectedPhoneId === p.id;
          if (original.isPrimary !== newIsPrimary) return true;
        }
        return false;
      });

      const createResults = await Promise.allSettled(
        phonesToCreate.map((item) => {
          const raw = (item.phone || "").trim();
          const e164 = toE164(raw);
          if (!e164Regex.test(e164)) {
            return Promise.reject(
              new Error("Phone must be in E.164 format (+[country][number])")
            );
          }
          const isPrimary = selectedPhoneId === item.id;
          return createPhone(contactId, {
            phone_type: "CELLULAR",
            phone_number: e164,
            is_primary: isPrimary,
          });
        })
      );

      const updateResults = await Promise.allSettled(
        phonesToUpdate.map((item) => {
          const original = contextPhones.find(
            (p) => p.partyPhoneUuid === item.id
          );
          if (!original) return Promise.resolve();

          const updateData = {};
          const newIsPrimary = selectedPhoneId === item.id;

          if (item.isEditing && validPhones[item.id]) {
            const raw = (item.phone || "").trim();
            const e164 = toE164(raw);
            if (!e164Regex.test(e164)) {
              return Promise.reject(
                new Error("Phone must be in E.164 format (+[country][number])")
              );
            }
            if (original.phoneNumber !== e164) {
              updateData.phone_number = e164;
            }
          }

          if (original.isPrimary !== newIsPrimary) {
            updateData.is_primary = newIsPrimary;
          }

          if (Object.keys(updateData).length === 0) return Promise.resolve();
          return updatePhone(contactId, item.id, updateData);
        })
      );

      const demoteOutsideResult = await (async () => {
        if (!originalPrimaryOutside) return { status: "fulfilled" };
        return updatePhone(contactId, originalPrimaryOutside.partyPhoneUuid, {
          is_primary: false,
        })
          .then(() => ({ status: "fulfilled" }))
          .catch((e) => ({ status: "rejected", reason: e }));
      })();

      const failures = [
        ...createResults,
        ...updateResults,
        demoteOutsideResult,
      ].filter((r) => r.status === "rejected");

      if (failures.length) {
        setShowErrorAlert(true);
        setStatusMessage(
          `Some changes couldn't be saved (${failures.length}). Please review and try again.`
        );
        return;
      }

      const selectedLocal = phones.find((p) => p.id === selectedPhoneId);
      const originalSelected = contextPhones.find(
        (cp) => cp.partyPhoneUuid === selectedPhoneId
      );
      const primaryChanged = originalSelected
        ? !originalSelected.isPrimary
        : !!selectedLocal?.isNew;

      if (primaryChanged) {
        const nextPrimaryE164 = selectedLocal?.phone
          ? toE164(selectedLocal.phone)
          : originalSelected?.phoneNumber || "";
        applyPrimaryPhoneLocally(selectedPhoneId, nextPrimaryE164);

        setSuccessMessage("Primary contact updated successfully.");
        setShowSuccessAlert(true);
        successTimeoutRef.current = setTimeout(() => {
          setShowSuccessAlert(false);
          setSuccessMessage("");
          successTimeoutRef.current = null;
          handleClose();
        }, 1200);
      } else if (phonesToCreate.length > 0) {
        setSuccessMessage("New phone successfully added");
        setShowSuccessAlert(true);
        successTimeoutRef.current = setTimeout(() => {
          setShowSuccessAlert(false);
          setSuccessMessage("");
          successTimeoutRef.current = null;
          handleClose();
        }, 1200);
      } else {
        handleClose();
      }
    } catch (err) {
      console.error("Error saving phones:", err);
      setShowErrorAlert(true);
      setStatusMessage("Error saving phones. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="medium"
        closeFromOutside={false}
      >
        <Modal.Header>
          <Modal.Header.Title>Phone Numbers</Modal.Header.Title>
          <Modal.Header.Description>
            These are other phone numbers of this client. You can select which
            one is the main one.
          </Modal.Header.Description>
        </Modal.Header>

        <Modal.Body>
          <HeaderPhoneModalContainer>
            <PhoneItemsWrapper>
              {phones.map((phoneItem) => {
              const isSelected = selectedPhoneId === phoneItem.id;
              const isValid = validPhones[phoneItem.id];
              const hasError = !!phoneErrors[phoneItem.id];
              const shouldShowMessage =
                phoneItem.isEditing && isValid && !hasError;

              const inlineMessage = hasError
                ? phoneErrors[phoneItem.id]
                : shouldShowMessage
                ? "Press enter to add it"
                : null;

              return (
                <React.Fragment key={phoneItem.id}>
                  <PhoneItemContainer
                    hasError={hasError}
                    isValid={isValid}
                    isEditing={phoneItem.isEditing}
                  >
                    <SideIconWrapper>
                      <RadioButton
                        type="radio"
                        name="phoneSelection"
                        value={phoneItem.id}
                        checked={isSelected}
                        onChange={(e) => handlePhoneSelection(e.target.value)}
                      />
                    </SideIconWrapper>

                    <PhoneInputContainer style={{ flexDirection: "column" }}>
                      {phoneItem.isEditing ? (
                        <InputText
                          type="tel"
                          placeholder="Enter phone number"
                          value={phoneItem.phone}
                          onChange={(e) =>
                            handlePhoneChange(phoneItem.id, e.target.value)
                          }
                          onKeyDown={(e) => handleKeyDown(e, phoneItem.id)}
                          autoFocus
                          height="36px"
                          width="100%"
                          displayError={false}
                          displaySuccess={false}
                          style={{
                            border: "none",
                            outline: "none",
                            boxShadow: "none",
                          }}
                        />
                      ) : (
                        <span>{phoneItem.phone}</span>
                      )}
                    </PhoneInputContainer>

                    <SideIconWrapper>
                      <Icon
                        name="delete"
                        onClick={() => handleDeletePhone(phoneItem.id)}
                        style={{
                          cursor: isDeleting ? "not-allowed" : "pointer",
                          opacity: isDeleting ? 0.5 : 1,
                        }}
                      />
                    </SideIconWrapper>
                  </PhoneItemContainer>
                  {inlineMessage && (
                    <ValidationMessage type={hasError ? "error" : "success"}>
                      {inlineMessage}
                    </ValidationMessage>
                  )}
                </React.Fragment>
              );
            })}
            </PhoneItemsWrapper>

            <AddPhoneContainer onClick={handleAddPhone} disabled={!canAdd}>
              <Icon name="plus" />
            </AddPhoneContainer>
          </HeaderPhoneModalContainer>
        </Modal.Body>

        <Modal.Actions align="end">
          <Modal.Button
            label={isSaving ? "Saving..." : "Save"}
            onClick={handleSave}
            disabled={!canSave || isSaving}
          />
        </Modal.Actions>
      </Modal>

      {(showSuccessAlert || showErrorAlert) && (
        <FloatingAlertContainer>
          {showSuccessAlert && (
            <Alert
              severity="success"
              title={
                successMessage.includes("Primary")
                  ? "Primary contact updated successfully."
                  : "New phone successfully added"
              }
              message={successMessage}
              onClose={() => {
                setShowSuccessAlert(false);
                setSuccessMessage("");
                if (successTimeoutRef.current) {
                  clearTimeout(successTimeoutRef.current);
                  successTimeoutRef.current = null;
                }
              }}
            />
          )}
          {showErrorAlert && (
            <Alert
              severity="error"
              title="Please complete or fix the pending phone fields."
              message={
                statusMessage ||
                "You must resolve all open or invalid fields before continuing."
              }
              onClose={() => {
                setShowErrorAlert(false);
                setStatusMessage("");
              }}
            />
          )}
        </FloatingAlertContainer>
      )}
    </>
  );
};

export default HeaderPhoneModal;
